import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { getToken, getUser, getRole, getTokenExpiry, getRememberFlag, logout as serviceLogout } from '../services/auth';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);

  const syncFromStorage = useCallback(() => {
    const storedToken = getToken();
    const storedUser = getUser();
    const storedRole = getRole();
    const storedExpiry = getTokenExpiry();
    const storedRemember = getRememberFlag();

    setToken(storedToken || null);
    setUser(storedUser || null);
    setTokenExpiresAt(storedExpiry ?? null);
    setRememberMe(!!storedRemember);

    if (storedRole) {
      setRole(storedRole);
    } else if (storedUser?.role) {
      setRole(storedUser.role);
    } else {
      setRole(null);
    }
  }, []);

  const logout = useCallback(({ redirect = true } = {}) => {
    serviceLogout({ redirect });
    setToken(null);
    setUser(null);
    setRole(null);
    setTokenExpiresAt(null);
    setRememberMe(false);
  }, []);

  useEffect(() => {
    syncFromStorage();

    const handleStorage = (event) => {
      if (!event || !event.key) {
        return;
      }
      if (['token', 'role', 'user', 'tokenExpiresAt', 'rememberMe'].includes(event.key)) {
        syncFromStorage();
      }
    };

    const handleAuthCleared = () => {
      syncFromStorage();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('auth:cleared', handleAuthCleared);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('auth:cleared', handleAuthCleared);
    };
  }, [syncFromStorage]);

  useEffect(() => {
    if (!token || !tokenExpiresAt) return;
    const now = Date.now();
    if (tokenExpiresAt <= now) {
      logout({ redirect: true });
      return;
    }
    const timeout = setTimeout(() => logout({ redirect: true }), tokenExpiresAt - now);
    return () => clearTimeout(timeout);
  }, [token, tokenExpiresAt, logout]);

  const value = useMemo(() => ({
    token,
    user,
    role,
    tokenExpiresAt,
    rememberMe,
    setToken,
    setUser,
    setRole,
    refreshAuth: syncFromStorage,
    logout,
  }), [token, user, role, tokenExpiresAt, rememberMe, syncFromStorage, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
